import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';
import { DynamicWidgetsService } from './dynamic-widgets.service';
import { NotificationService } from './notification.service';
import { WidgetContextService, WidgetContext } from './widget-context.service';
import { FileWatchService, FileChangeEvent } from './file-watch.service';
import { IdentityService } from './identity.service';
import { SettingsService } from './settings.service';
import { WidgetService } from './widget.service';

/**
 * File item returned from directory listing
 */
export interface FileItem {
  name: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
}

/**
 * Grid operations exposed to external apps — open widgets in the main layout.
 */
export interface GridBridge {
  openFile(path: string): void;
  openMarkdown(path: string, title: string): void;
  openCsv(path: string, title: string): void;
  previewFile(path: string, type: 'image' | 'pdf'): void;
  openWorkSession(params: { sessionId: string; agentId: string; title: string; channelId?: string; warmUp?: string }): void;
  /** Show a count on this widget's tab (null clears). */
  setBadge?(count: number | null): void;
  /** Bring this widget's panel to the front (notification click). */
  focusSelf?(): void;
}

/**
 * Optional extras passed to createHost() from the widget layer.
 */
export interface HostExtras {
  /** Returns a fresh auth token for WebSocket connections. */
  getToken?: () => Promise<string>;
  /** Bridge to the grid for opening file viewers, work sessions, etc. */
  grid?: GridBridge;
  /** The app's manifest name — required for `functions.call` (its /fn/ route
   *  is keyed by app name, not widget id). */
  appName?: string;
}

/**
 * Host interface provided to external web component apps.
 * This is the contract between lit-server and external apps.
 */
export interface LitWidgetHost {
  // Widget identity
  widgetId: string;
  teamName: string;

  // File operations (via mux commander, respects user permissions)
  files: {
    read(path: string): Promise<string>;
    write(path: string, content: string): Promise<void>;
    list(path: string): Promise<FileItem[]>;
    watch(path: string, callback: (event: FileChangeEvent) => void): () => void;
  };

  // Claude context - lets the app tell Claude what state it's in
  context: {
    update(state: Record<string, any>): void;
    setFocused(): void;
  };

  // Current user info
  user: {
    username: string;
  };

  // Base path configured for this app instance
  basePath: string;

  // Current theme ('dark' or 'light')
  theme: string;

  // Subscribe to theme changes; returns unsubscribe function
  onThemeChange(callback: (theme: string) => void): () => void;

  // Mux server URL for direct HTTP/WebSocket calls
  muxUrl: string;

  // Auth helpers
  auth: {
    /** Returns a fresh access token, refreshing if needed. */
    getToken(): Promise<string>;
  };

  // Real-time events (SSE pub/sub)
  events: {
    subscribe(topic: string, callback: (event: any) => void): () => void;
    publish(topic: string, data: any): Promise<void>;
  };

  // Grid bridge — open viewers and work sessions; null if grid not available
  grid: GridBridge | null;

  // Agent bridge — run a one-shot prompt through one of YOUR OWN agents and get
  // the text back. Uses the agent's backend (the subscription path Anthropic
  // accepts), stateless (no chat session/history). The server rejects any
  // attempt to ask an agent you don't own.
  agent: {
    ask(agentId: string, opts: { prompt: string; system?: string }): Promise<string>;
    list(): Promise<Array<{ agentId: string; name: string; backend: string; model: string; healthy: boolean }>>;
  };

  // Unread/attention count on the app's launcher row, rail pin and open
  // tab. null clears; >99 renders "99+". Per browser tab, not persisted.
  badge: {
    set(count: number | null): void;
  };

  // A platform toast, or a browser notification when the page is hidden
  // (the platform asks for permission, the app never does). Same `tag`
  // collapses repeats. Rate-limited: one per tag per 5 s, 10/min per app.
  notify(opts: { title: string; body?: string; tag?: string; onClick?: () => void }): void;

  // The app's own backend: functions declared in app.yaml (`functions:`),
  // run by the platform as the user (POST /mux/apps/{app}/fn/{name}). A
  // handler error rejects with its message; a function bound to a hook is
  // not callable from here.
  functions: {
    call(name: string, params?: Record<string, any>): Promise<any>;
  };

  // The user's Workspace (their agent channels) in this team. `openChannel`
  // brings it forward on one channel: a directory app passes the `channel_id`
  // the platform returned for an agent someone opened to this user.
  workspace: {
    openChannel(channelId: string): void;
  };
}

/**
 * Manifest format for external apps (app.yaml)
 */
export interface AppManifest {
  name: string;
  title: string;
  description?: string;
  icon?: string;
  version?: string;

  // Entry point - the web component tag name
  element: string;

  // JS/CSS files to load (relative to app directory)
  scripts: string[];
  styles?: string[];

  // Default configuration
  config?: Record<string, any>;
}

/**
 * Discovered external app
 */
export interface ExternalApp {
  id: string;           // Unique ID (e.g., 'team:app-name')
  manifest: AppManifest;
  source: 'user' | 'team' | 'system';
  basePath: string;     // Path to the app directory
  loaded: boolean;      // Whether scripts have been loaded
}

@Injectable({
  providedIn: 'root'
})
export class ExternalAppService {
  private _apps$ = new BehaviorSubject<ExternalApp[]>([]);
  private _loading$ = new BehaviorSubject<boolean>(false);
  private loadedScripts = new Set<string>();

  public readonly apps$ = this._apps$.asObservable();
  public readonly loading$ = this._loading$.asObservable();

  constructor(
    private http: HttpClient,
    private dynamicWidgets: DynamicWidgetsService,
    private notifications: NotificationService,
    private widgetContext: WidgetContextService,
    private fileWatch: FileWatchService,
    private identity: IdentityService,
    private settings: SettingsService,
    private widgets: WidgetService
  ) {}

  private get currentUsername(): string {
    return this.identity.me?.username || 'anonymous';
  }

  /**
   * Discover external apps from known locations
   */
  async discoverApps(teamName: string): Promise<ExternalApp[]> {
    this._loading$.next(true);
    const apps: ExternalApp[] = [];

    // Search paths for apps
    // Local mode: apps live at ~/.lit/data/local/apps (the data root + "apps")
    // Multi-tenant: apps live at /data/{team}/apps
    const teamAppsPath = ConfigService.localMode ? `~/.lit/data/${teamName}/apps` : `/data/${teamName}/apps`;
    const searchPaths: { path: string; source: 'team' | 'user' }[] = [
      { path: teamAppsPath, source: 'team' },
      { path: '~/apps', source: 'user' },
    ];

    for (const { path, source } of searchPaths) {
      try {
        const response = await firstValueFrom(
          this.http.post<any>(`${ConfigService.muxUrl}/commander/list_directory`, { path })
        );

        if (response.success) {
          for (const item of response.data) {
            if (item.type === 'directory') {
              const appPath = `${path}/${item.name}`;
              const manifest = await this.loadManifest(appPath);
              if (manifest) {
                apps.push({
                  id: `${source}:${item.name}`,
                  manifest,
                  source,
                  basePath: appPath,
                  loaded: false
                });
              }
            }
          }
        }
      } catch {
        // Path doesn't exist, that's OK
      }
    }

    this._apps$.next(apps);
    this._loading$.next(false);
    return apps;
  }

  /**
   * Load app manifest from directory
   */
  private async loadManifest(appPath: string): Promise<AppManifest | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${ConfigService.muxUrl}/commander/read_file`, {
          path: `${appPath}/app.yaml`
        })
      );

      if (response.success) {
        return this.parseYaml(response.data);
      }
    } catch {
      // No manifest
    }
    return null;
  }

  /**
   * Load an external app's scripts and styles
   */
  async loadApp(app: ExternalApp): Promise<void> {
    if (app.loaded) return;

    // Load styles
    if (app.manifest.styles) {
      for (const style of app.manifest.styles) {
        await this.loadStyle(`${app.basePath}/${style}`, app.id);
      }
    }

    // Load scripts
    for (const script of app.manifest.scripts) {
      await this.loadScript(`${app.basePath}/${script}`);
    }

    app.loaded = true;
  }

  /**
   * Create a host object for a web component instance
   */
  createHost(
    widgetId: string,
    teamName: string,
    basePath: string,
    extras?: HostExtras
  ): LitWidgetHost {
    const self = this;

    return {
      widgetId,
      teamName,
      basePath,

      muxUrl: ConfigService.muxUrl,

      auth: {
        async getToken(): Promise<string> {
          if (extras?.getToken) {
            return extras.getToken();
          }
          // Fallback: read from localStorage (set by keycloak-angular)
          return localStorage.getItem('id_token') || '';
        }
      },

      grid: extras?.grid ?? null,

      files: {
        async read(path: string): Promise<string> {
          const response = await firstValueFrom(
            self.http.post<any>(`${ConfigService.muxUrl}/commander/read_file`, { path })
          );
          if (response.success) {
            return response.data;
          }
          throw new Error(response.error || 'Failed to read file');
        },

        async write(path: string, content: string): Promise<void> {
          const response = await firstValueFrom(
            self.http.post<any>(`${ConfigService.muxUrl}/commander/write_file`, { path, content })
          );
          if (!response.success) {
            throw new Error(response.error || 'Failed to write file');
          }
        },

        async list(path: string): Promise<FileItem[]> {
          const response = await firstValueFrom(
            self.http.post<any>(`${ConfigService.muxUrl}/commander/list_directory`, { path })
          );
          if (response.success) {
            return response.data;
          }
          throw new Error(response.error || 'Failed to list directory');
        },

        watch(path: string, callback: (event: FileChangeEvent) => void): () => void {
          self.fileWatch.watchFile(path);
          const subscription = self.fileWatch.getChanges(path).subscribe(callback);
          return () => {
            subscription.unsubscribe();
            self.fileWatch.unwatchFile(path);
          };
        }
      },

      context: {
        update(state: Record<string, any>): void {
          const context: WidgetContext = {
            widgetId,
            widgetType: 'ExternalApp',
            path: basePath,
            state
          };
          self.widgetContext.updateContext(context);
        },

        setFocused(): void {
          self.widgetContext.setFocused(widgetId);
        }
      },

      user: {
        username: self.currentUsername
      },

      get theme() {
        return self.settings.theme;
      },

      onThemeChange(callback: (theme: string) => void): () => void {
        const sub = self.settings.theme$.subscribe(callback);
        return () => sub.unsubscribe();
      },

      events: {
        subscribe(topic: string, callback: (event: any) => void): () => void {
          const es = new EventSource(`${ConfigService.muxUrl}/events/${encodeURIComponent(topic)}/stream`);
          es.addEventListener('message', (e: MessageEvent) => {
            try {
              callback(JSON.parse(e.data));
            } catch { /* parse error */ }
          });
          return () => es.close();
        },

        async publish(topic: string, data: any): Promise<void> {
          await firstValueFrom(
            self.http.post<any>(`${ConfigService.muxUrl}/events/${encodeURIComponent(topic)}`, { data })
          );
        }
      },

      badge: {
        set(count: number | null): void {
          if (extras?.appName) self.dynamicWidgets.setBadge(extras.appName, count);
          extras?.grid?.setBadge?.(count);
        }
      },

      notify(opts: { title: string; body?: string; tag?: string; onClick?: () => void }): void {
        if (!opts?.title) return;
        self.notifications.notifyFromApp(extras?.appName || widgetId, {
          ...opts,
          onClick: () => { extras?.grid?.focusSelf?.(); opts.onClick?.(); },
        });
      },

      functions: {
        async call(name: string, params?: Record<string, any>): Promise<any> {
          const appName = extras?.appName;
          if (!appName) throw new Error('functions.call: host was created without an appName');
          try {
            const response = await firstValueFrom(
              self.http.post<any>(
                `${ConfigService.muxUrl}/apps/${encodeURIComponent(appName)}/fn/${encodeURIComponent(name)}?team=${encodeURIComponent(teamName)}`,
                { params: params ?? {} }
              )
            );
            return response?.result;
          } catch (e: any) {
            const msg = e?.error?.error || e?.error?.detail || e?.message || 'function call failed';
            throw new Error(msg);
          }
        }
      },

      workspace: {
        openChannel(channelId: string): void {
          if (channelId) self.widgets.requestChannel(teamName, channelId);
        }
      },

      agent: {
        async ask(agentId: string, opts: { prompt: string; system?: string }): Promise<string> {
          const response = await firstValueFrom(
            self.http.post<any>(
              `${ConfigService.muxUrl}/agents/${encodeURIComponent(agentId)}/ask`,
              { prompt: opts.prompt, system: opts.system ?? null }
            )
          );
          return response?.text ?? '';
        },

        async list(): Promise<Array<{ agentId: string; name: string; backend: string; model: string; healthy: boolean }>> {
          const response = await firstValueFrom(
            self.http.get<any>(`${ConfigService.muxUrl}/agents/roster?team=${encodeURIComponent(teamName)}`)
          );
          return (response?.agents ?? []).map((a: any) => ({
            agentId: a.agent_id, name: a.name, backend: a.backend, model: a.model, healthy: a.healthy
          }));
        }
      }
    };
  }

  /**
   * Clean up when an external app widget is destroyed
   */
  destroyHost(widgetId: string): void {
    this.widgetContext.removeContext(widgetId);
  }

  /**
   * Load a script from the filesystem via mux
   */
  private async loadScript(path: string): Promise<void> {
    if (this.loadedScripts.has(path)) return;

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${ConfigService.muxUrl}/commander/read_file`, { path })
      );

      if (response.success) {
        // Create and execute script
        const script = document.createElement('script');
        script.type = 'module';
        script.textContent = response.data;
        document.head.appendChild(script);
        this.loadedScripts.add(path);
      }
    } catch (err) {
      console.error(`Failed to load script: ${path}`, err);
    }
  }

  /**
   * Load a stylesheet from the filesystem via mux
   */
  private async loadStyle(path: string, appId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${ConfigService.muxUrl}/commander/read_file`, { path })
      );

      if (response.success) {
        const style = document.createElement('style');
        style.setAttribute('data-app', appId);
        style.textContent = response.data;
        document.head.appendChild(style);
      }
    } catch (err) {
      console.error(`Failed to load style: ${path}`, err);
    }
  }

  /**
   * Simple YAML parser for manifests
   */
  private parseYaml(content: string): any {
    const result: any = {};
    const lines = content.split('\n');
    let currentKey = '';
    let inArray = false;
    let arrayKey = '';
    let arrayItems: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;

      // Array item
      if (trimmed.startsWith('- ')) {
        inArray = true;
        arrayItems.push(trimmed.substring(2).trim());
        continue;
      }

      // If we were in an array and hit a non-array line, save it
      if (inArray && !line.startsWith(' ')) {
        result[arrayKey] = arrayItems;
        arrayItems = [];
        inArray = false;
      }

      // Key: value pair
      const colonIdx = trimmed.indexOf(':');
      if (colonIdx > 0) {
        const key = trimmed.substring(0, colonIdx).trim();
        const value = trimmed.substring(colonIdx + 1).trim();

        if (value) {
          // Remove quotes if present
          result[key] = value.replace(/^["']|["']$/g, '');
        } else {
          // Possibly an array or object follows
          arrayKey = key;
          currentKey = key;
        }
      }
    }

    // Don't forget trailing array
    if (inArray && arrayItems.length > 0) {
      result[arrayKey] = arrayItems;
    }

    return result;
  }
}
